import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type {
  DefinedUseQueryResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import type { QueryBody } from '@jane/shared/data-access';
import { janeApi, safeParseAsync } from '@jane/shared/data-access';
import type { Kiosk } from '@jane/shared/types';

import { useGetJwt, useSaveJwt } from './jwt';
import { zKiosk } from './kiosk.types';

type WhoAmiQueryBody = QueryBody<Kiosk | null, unknown, Kiosk | null>;
type QueryOptions = Pick<
  WhoAmiQueryBody,
  'enabled' | 'staleTime' | 'initialData'
>;

type GetWhoAmiReturnType = {
  kiosk: Kiosk | null;
};

type WhoAmiQueryBodyParams = {
  cartUuid?: string;
  jwt?: string | null;
  options?: QueryOptions;
};

const getWhoami = async (jwt?: string | null, cartUuid?: string) => {
  const url = `/kiosk/whoami${cartUuid ? `?cart_uuid=${cartUuid}` : ''}`;

  const response = await janeApi.get<GetWhoAmiReturnType>(url, jwt);

  return await safeParseAsync(zKiosk, response.kiosk, 'getWhoami');
};

/** Query Body Overloads */

export function getWhoamiQueryBody(
  jwt: WhoAmiQueryBodyParams['jwt'],
  cartUuid: WhoAmiQueryBodyParams['cartUuid'],
  options?: Omit<QueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  }
): WhoAmiQueryBody;

export function getWhoamiQueryBody(
  jwt: WhoAmiQueryBodyParams['jwt'],
  cartUuid: WhoAmiQueryBodyParams['cartUuid'],
  options?: QueryOptions
): WhoAmiQueryBody;

/** Query Body Implementation */

export function getWhoamiQueryBody(
  jwt: WhoAmiQueryBodyParams['jwt'],
  cartUuid: WhoAmiQueryBodyParams['cartUuid'],
  options = {}
): WhoAmiQueryBody {
  return {
    queryFn: () => getWhoami(jwt, cartUuid),
    queryKey: ['whoami', jwt, cartUuid],
    ...options,
  };
}

export function useGetWhoami(props: {
  cartUuid?: string;
  options?: Omit<QueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  };
}): UseQueryResult<Kiosk | null, unknown>;

export function useGetWhoami(props: {
  cartUuid?: string;
  options?: Omit<QueryOptions, 'initialData'> & {
    initialData?: Kiosk | null | (() => Kiosk | null);
  };
}): DefinedUseQueryResult<Kiosk, unknown>;

/** Hooks */

export function useGetWhoami({
  cartUuid,
  options,
}: {
  cartUuid?: string;
  options?: object;
}) {
  const location = useLocation();
  const mobilePath = location.pathname.includes('/mobile');
  const { data: jwt, isSuccess } = useGetJwt();
  const { mutate: saveJwt } = useSaveJwt();
  const kioskDeviceExpirationRedirectEnabled = useFlag(
    FLAGS.c3KioskDeviceExpirationRedirectEnabled
  );

  const query = useQuery(
    getWhoamiQueryBody(jwt, cartUuid, {
      enabled: isSuccess && !mobilePath,
      staleTime: kioskDeviceExpirationRedirectEnabled
        ? 5 * 60 * 1000 // 5 minutes
        : 24 * 60 * 60 * 1000, // 24 hours
      ...options,
    })
  );

  useEffect(() => {
    // Clear (expired) jwt if it results in an empty whoami
    if (jwt && query.isSuccess && !query.data?.id && !mobilePath) {
      saveJwt(null);
    }
  }, [jwt, query, saveJwt, mobilePath]);

  return query;
}
